import Phaser from 'phaser';
import { Client, getStateCallbacks, type Room } from 'colyseus.js';
import { AREAS, GHOST_REACH, GHOST_SPOTS, MSG, hotspotAt, isWalkable, type AreaLayout } from '@bb/shared';

const EMOTE_ICON: Record<string, string> = { wave: '👋', heart: '❤️', laugh: '😄', dance: '💃', star: '⭐' };
import { AVATAR_H, AVATAR_W, avatarCanvas, lookKey, wearFrom, type AvatarLook } from '../art/avatar';
import type { ProgressNews } from '../net/api';
import { drawArea } from './world';
import { ghostCanvas } from '../art/ghost';

export interface AreaEvents {
  area: string;
  /** Decorations from live events, e.g. ['lanterns']. */
  decor: string[];
  /** Keys of events running now (event hotspots such as the Haunted House gate need one). */
  liveEvents: string[];
  /** The server confirmed a ghost catch in the Haunted House. */
  onGhost: (g: { caught: boolean; candy: number; found: number; total: number }) => void;
  /** A friend invited this player to a Squad game. */
  onSquadInvite: (invite: { roomId: string; from: string }) => void;
  catalog: Map<string, { render: Record<string, unknown> }>;
  onStatus: (s: { kind: 'connecting' | 'online' | 'closed'; message?: string; count?: number }) => void;
  onHotspot: (action: string, label: string) => void;
  /** A chat line to show in the log ({ name, text }). */
  onChat: (line: { name: string; text: string; mine: boolean }) => void;
  /** Private feedback from the server (rate limits, filter results). */
  onNotice: (message: string) => void;
  /** Another player's avatar was clicked. */
  onPlayer: (displayName: string) => void;
  /** Gives the UI a way to send chat, quick chat and emotes through this room. */
  bindSender: (send: ((type: string, payload: unknown) => void) | null) => void;
  /** Achievements etc. completed by something that happened in the room (e.g. visiting an area). */
  onProgress: (news: ProgressNews) => void;
}

interface PlayerState {
  name: string;
  characterType: string;
  species: string;
  appearance: string;
  equipped: string;
  x: number;
  y: number;
  tx: number;
  ty: number;
}

interface Avatar {
  root: Phaser.GameObjects.Container;
  sprite: Phaser.GameObjects.Image;
  bubble?: Phaser.GameObjects.Text;
  bubbleTimer?: Phaser.Time.TimerEvent;
  state: PlayerState;
  lookSig: string;
}

const CLOSE_MESSAGES: Record<number, string> = {
  4001: 'You opened Buddy Bay somewhere else, so this window was disconnected.',
  4003: 'Your access to Buddy Bay has changed. Please sign in again.',
  4005: "Play time is over for now. Your parent's time limits are set in parent controls.",
  4007: 'The Haunted House has closed for this year. See you next Halloween!',
  4008: 'The Buddy Bay team reset player progress. Rejoin to carry on with your fresh start!',
};

/** Distance from a hotspot at which walking there triggers it. */
const HOTSPOT_REACH = 70;

export class AreaScene extends Phaser.Scene {
  private room?: Room;
  /** Set when the scene is torn down; an in-flight join must then leave immediately. */
  private destroyed = false;
  private avatars = new Map<string, Avatar>();
  private marker?: Phaser.GameObjects.Arc;
  private opts!: AreaEvents;
  private layout!: AreaLayout;
  /** Hotspot we're walking toward; fires when we arrive. */
  private pending?: { action: string; label: string; x: number; y: number; at: number };
  /** Haunted House ghosts not caught yet. */
  private ghosts = new Map<string, Phaser.GameObjects.Image>();

  constructor() {
    super('area');
  }

  init(data: AreaEvents) {
    this.opts = data;
    this.layout = AREAS[data.area]!;
  }

  create() {
    drawArea(this, this.layout, this.opts.decor, this.opts.liveEvents);
    this.marker = this.add.circle(0, 0, 9).setStrokeStyle(3, 0xe9a915).setVisible(false).setDepth(1);
    // Phaser caches the canvas position; the page layout can shift (the HUD fills in,
    // panels open), which would make clicks land in the wrong place. Keep it fresh.
    const canvas = this.game.canvas;
    const refreshBounds = () => this.scale.updateBounds();
    canvas.addEventListener('pointerdown', refreshBounds, { capture: true });
    this.time.addEvent({ delay: 500, loop: true, callback: refreshBounds });
    this.sys.events.once(Phaser.Scenes.Events.DESTROY, () => canvas.removeEventListener('pointerdown', refreshBounds, { capture: true }));
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onClick(p.worldX, p.worldY));
    this.sys.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.destroyed = true;
      this.leaveRoom();
    });
    void this.connect();
  }

  private async connect() {
    this.opts.onStatus({ kind: 'connecting' });
    // A scene torn down right after creation (React StrictMode, fast area switches)
    // must never join: joining would replace this player's live connection.
    await new Promise((r) => setTimeout(r, 60));
    if (this.destroyed) return;
    try {
      const client = new Client(`${window.location.origin}/realtime`);
      const room = await client.joinOrCreate(this.layout.key);
      if (this.destroyed) {
        void room.leave(true);
        return;
      }
      this.room = room;
      const players = getStateCallbacks(room)(room.state).players as unknown as {
        onAdd: (cb: (p: PlayerState, key: string) => void) => void;
        onRemove: (cb: (p: PlayerState, key: string) => void) => void;
      };
      players.onAdd((player, key) => {
        this.addAvatar(key, player);
        this.reportCount();
      });
      players.onRemove((_p, key) => {
        this.avatars.get(key)?.root.destroy();
        this.avatars.delete(key);
        this.reportCount();
      });
      room.onMessage(MSG.say, (m: { id: string; text: string }) => this.showSay(m.id, m.text));
      room.onMessage(MSG.emoted, (m: { id: string; emote: string }) => this.showEmote(m.id, m.emote));
      room.onMessage(MSG.notice, (m: { message: string }) => this.opts.onNotice(m.message));
      room.onMessage(MSG.progress, (news: ProgressNews) => this.opts.onProgress(news));
      room.onMessage(MSG.squadInvite, (m: { roomId: string; from: string }) => this.opts.onSquadInvite(m));
      room.onMessage(MSG.ghosts, (m: { found: string[] }) => this.placeGhosts(m.found));
      room.onMessage(MSG.ghostFound, (m: { key: string; caught: boolean; candy: number; found: number; total: number }) => {
        const g = this.ghosts.get(m.key);
        if (g) {
          this.ghosts.delete(m.key);
          this.tweens.add({ targets: g, y: g.y - 80, alpha: 0, scale: 1.4, duration: 700, ease: 'Sine.easeOut', onComplete: () => g.destroy() });
        }
        this.opts.onGhost(m);
      });
      this.opts.bindSender((type, payload) => room.send(type, payload));
      room.onLeave((code) => {
        this.opts.bindSender(null);
        if (code === 1000 || this.destroyed) return; // we left on purpose (area change)
        this.opts.onStatus({ kind: 'closed', message: CLOSE_MESSAGES[code] ?? 'You were disconnected. Rejoin to keep playing.' });
      });
      this.reportCount();
    } catch (e) {
      if (this.destroyed) return;
      this.opts.onStatus({ kind: 'closed', message: (e as Error).message || 'Could not connect.' });
    }
  }

  private reportCount() {
    this.opts.onStatus({ kind: 'online', count: this.avatars.size });
  }

  private suppressWalk = false;

  private onClick(x: number, y: number) {
    if (this.suppressWalk) {
      this.suppressWalk = false;
      return;
    }
    const spot = hotspotAt(this.layout, x, y, this.opts.liveEvents);
    if (spot) {
      // Walk to the nearest walkable point below the hotspot, then trigger it.
      const tx = Math.min(Math.max(spot.rect.x + spot.rect.w / 2, 60), this.layout.width - 60);
      let ty = spot.rect.y + spot.rect.h + 20;
      while (!isWalkable(this.layout, tx, ty) && ty > 0) ty -= 10;
      if (!isWalkable(this.layout, tx, ty)) ty = spot.rect.y + spot.rect.h / 2;
      this.pending = { action: spot.action, label: spot.label, x: tx, y: ty, at: this.time.now };
      this.walkTo(tx, ty);
      return;
    }
    this.pending = undefined;
    this.walkTo(x, y);
  }

  /** Ghosts hide around the Haunted House: faint from afar, clear when you're close enough to catch one. */
  private placeGhosts(found: string[]) {
    if (!this.textures.exists('ghost')) this.textures.addCanvas('ghost', ghostCanvas(64));
    for (const spot of GHOST_SPOTS) {
      if (found.includes(spot.key) || this.ghosts.has(spot.key)) continue;
      const img = this.add.image(spot.x, spot.y - 30, 'ghost').setAlpha(0.12).setDepth(9).setInteractive({ useHandCursor: true });
      this.tweens.add({ targets: img, y: spot.y - 42, yoyo: true, repeat: -1, duration: 1400 + Math.random() * 600, ease: 'Sine.easeInOut' });
      img.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.suppressWalk = true;
        const me = this.room ? this.avatars.get(this.room.sessionId) : undefined;
        if (me && Math.hypot(me.state.x - spot.x, me.state.y - spot.y) <= GHOST_REACH) this.room!.send(MSG.findGhost, { key: spot.key });
        else {
          // Too far: walk over first, then click again.
          this.suppressWalk = false;
          this.opts.onNotice('Walk closer to catch that ghost!');
          this.walkTo(spot.x, Math.min(spot.y + 20, this.layout.walkable[0]!.y + this.layout.walkable[0]!.h - 10));
        }
      });
      this.ghosts.set(spot.key, img);
    }
  }

  private walkTo(x: number, y: number) {
    if (!this.room || !isWalkable(this.layout, x, y)) return;
    this.room.send(MSG.moveTo, { x, y });
    this.marker!.setPosition(x, y).setVisible(true).setAlpha(1);
    this.tweens.add({ targets: this.marker, alpha: 0, duration: 900, ease: 'Sine.easeIn' });
  }

  private look(state: PlayerState): AvatarLook {
    return {
      characterType: state.characterType,
      species: state.species || null,
      appearance: safeJson(state.appearance),
      wear: wearFrom(safeJson(state.equipped), this.opts.catalog),
    };
  }

  private textureFor(look: AvatarLook): string {
    const key = lookKey(look);
    if (!this.textures.exists(key)) this.textures.addCanvas(key, avatarCanvas(look, 1));
    return key;
  }

  private addAvatar(key: string, state: PlayerState) {
    const look = this.look(state);
    const sprite = this.add.image(0, 0, this.textureFor(look)).setOrigin(0.5, 1);
    const isMe = key === this.room?.sessionId;
    const label = this.add
      .text(0, -AVATAR_H - 4, state.name, {
        fontFamily: '"Baloo 2", system-ui, sans-serif',
        fontSize: '15px',
        fontStyle: '700',
        color: isMe ? '#14506e' : '#1d2b36',
        backgroundColor: isMe ? '#fdf1d2' : '#ffffffd9',
        padding: { x: 6, y: 1 },
      })
      .setOrigin(0.5, 1);
    const root = this.add.container(state.x, state.y, [sprite, label]);
    root.setSize(AVATAR_W, AVATAR_H);
    if (!isMe) {
      // Clicking another player opens their profile instead of walking.
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.suppressWalk = true;
        this.opts.onPlayer(state.name);
      });
    }
    this.avatars.set(key, { root, sprite, state, lookSig: state.appearance + state.equipped });
  }

  override update(_t: number, dt: number) {
    const k = Math.min(1, dt / 90);
    for (const [key, a] of this.avatars) {
      const r = a.root;
      r.x += (a.state.x - r.x) * k;
      r.y += (a.state.y - r.y) * k;
      if (Math.abs(a.state.x - r.x) > 200 || Math.abs(a.state.y - r.y) > 200) r.setPosition(a.state.x, a.state.y);
      r.setDepth(10 + r.y);
      if (key === this.room?.sessionId && this.ghosts.size) {
        for (const [gk, img] of this.ghosts) {
          const spot = GHOST_SPOTS.find((g) => g.key === gk)!;
          const near = Math.hypot(a.state.x - spot.x, a.state.y - spot.y) <= GHOST_REACH;
          img.setAlpha(near ? 0.95 : 0.12);
        }
      }
      const sig = a.state.appearance + a.state.equipped;
      if (sig !== a.lookSig) {
        a.lookSig = sig;
        a.sprite.setTexture(this.textureFor(this.look(a.state)));
      }
      const moving = Math.hypot(a.state.tx - a.state.x, a.state.ty - a.state.y) > 1;
      a.sprite.y = moving ? -Math.abs(Math.sin(this.time.now / 90)) * 4 : 0;
      if (a.state.tx < a.state.x - 1) a.sprite.setFlipX(true);
      else if (a.state.tx > a.state.x + 1) a.sprite.setFlipX(false);

      // Give the server a moment to start the walk before judging arrival.
      if (key === this.room?.sessionId && this.pending && !moving && this.time.now - this.pending.at > 400) {
        const d = Math.hypot(a.state.x - this.pending.x, a.state.y - this.pending.y);
        const p = this.pending;
        this.pending = undefined;
        if (d < HOTSPOT_REACH) this.opts.onHotspot(p.action, p.label);
      }
    }
  }

  private showSay(id: string, text: string) {
    const a = this.avatars.get(id);
    if (!a) return;
    this.opts.onChat({ name: a.state.name, text, mine: id === this.room?.sessionId });
    a.bubbleTimer?.remove();
    a.bubble?.destroy();
    a.bubble = this.add
      .text(0, -AVATAR_H - 30, text, {
        fontFamily: 'Figtree, system-ui, sans-serif',
        fontSize: '15px',
        color: '#12283a',
        backgroundColor: '#ffffff',
        padding: { x: 9, y: 5 },
        wordWrap: { width: 220 },
        align: 'center',
      })
      .setOrigin(0.5, 1);
    a.root.add(a.bubble);
    a.bubbleTimer = this.time.delayedCall(6000, () => {
      a.bubble?.destroy();
      a.bubble = undefined;
    });
  }

  private showEmote(id: string, emote: string) {
    const a = this.avatars.get(id);
    if (!a) return;
    const icon = this.add.text(0, -AVATAR_H - 10, EMOTE_ICON[emote] ?? '✨', { fontSize: '30px' }).setOrigin(0.5, 1);
    a.root.add(icon);
    this.tweens.add({ targets: icon, y: icon.y - 40, alpha: 0, duration: 1600, ease: 'Sine.easeOut', onComplete: () => icon.destroy() });
    if (emote === 'dance') this.tweens.add({ targets: a.sprite, angle: { from: -12, to: 12 }, yoyo: true, repeat: 3, duration: 160, onComplete: () => a.sprite.setAngle(0) });
  }

  leaveRoom() {
    this.opts.bindSender(null);
    void this.room?.leave(true);
  }
}

function safeJson(s: string): Record<string, string> {
  try {
    return JSON.parse(s || '{}') as Record<string, string>;
  } catch {
    return {};
  }
}
